import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ANALYTICS_DATA, GetAnalyticsDataResponse } from '../../core/graphql/queries/analytics';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  makeStyles,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Snackbar,
  SnackbarContent,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@material-ui/core';
import {
  PlayArrow as PlayIcon,
  GetApp as DownloadIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Album as AlbumIcon,
  MusicNote as MusicIcon,
  TrendingUp as TrendingUpIcon
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#02172a',
    minHeight: '100vh',
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  headerTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1),
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  headerSubtitle: {
    color: '#b0b0b0',
    fontSize: '1.1rem',
    lineHeight: 1.5,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  filterContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(2),
    },
  },
  timeFrameFilter: {
    minWidth: 200,
    '& .MuiInputLabel-root': {
      color: '#b0b0b0',
    },
    '& .MuiOutlinedInput-root': {
      color: '#fff',
      '& fieldset': {
        borderColor: '#2a3a4a',
      },
      '&:hover fieldset': {
        borderColor: '#1976d2',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1976d2',
      },
    },
    '& .MuiSelect-icon': {
      color: '#b0b0b0',
    },
  },
  statsGrid: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    transition: 'all 0.2s ease-in-out',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 32px rgba(25, 118, 210, 0.15)',
      borderColor: '#1976d2',
    },
  },
  statCardContent: {
    padding: theme.spacing(3),
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
  },
  statNumber: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '2.25rem',
    lineHeight: 1,
    marginBottom: theme.spacing(0.5),
  },
  statLabel: {
    color: '#b0b0b0',
    fontSize: '0.9rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statIcon: {
    width: 48,
    height: 48,
    marginBottom: theme.spacing(2),
  },
  contentGrid: {
    marginBottom: theme.spacing(4),
  },
  section: {
    backgroundColor: '#1a2332',
    border: '1px solid #2a3a4a',
    borderRadius: 12,
    padding: theme.spacing(3),
    height: 'fit-content',
  },
  sectionTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    '&::after': {
      content: '""',
      flex: 1,
      height: '1px',
      backgroundColor: '#2a3a4a',
      marginLeft: theme.spacing(2),
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    color: '#fff',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    color: '#fff',
  },
}));

interface Track {
  name: string;
  plays: number;
  downloads: number;
}

interface Activity {
  type: string;
  track: string;
  time: string;
}

interface AnalyticsData {
  totalPlays: number;
  totalDownloads: number;
  totalEarnings: number;
  monthlyPlays: number;
  monthlyDownloads: number;
  monthlyEarnings: number;
  totalArtists: number;
  totalAlbums: number;
  totalSongs: number;
  topTracks: Track[];
  recentActivity: Activity[];
}

const AdminAnalytics: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [timeFrame, setTimeFrame] = useState<string>('all');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    totalPlays: 0,
    totalDownloads: 0,
    totalEarnings: 0,
    monthlyPlays: 0,
    monthlyDownloads: 0,
    monthlyEarnings: 0,
    totalArtists: 0,
    totalAlbums: 0,
    totalSongs: 0,
    topTracks: [],
    recentActivity: []
  });

  // Convert timeFrame to startDate and endDate for analytics query
  const getDateRange = (range: string): { startDate: string | null; endDate: string | null } => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    const endDate = today.toISOString().split('T')[0]; // Format as YYYY-MM-DD

    if (range === 'allTime' || range === 'all') {
      return { startDate: null, endDate: null };
    }
    
    if (range === 'today') {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      return { startDate: start.toISOString().split('T')[0], endDate };
    }
    
    if (range === 'week' || range === '7d') {
      const start = new Date();
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return { startDate: start.toISOString().split('T')[0], endDate };
    }
    
    if (range === 'month' || range === '30d') {
      const start = new Date();
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      return { startDate: start.toISOString().split('T')[0], endDate };
    }
    
    if (range === 'year' || range === '1y') {
      const start = new Date();
      start.setFullYear(start.getFullYear() - 1);
      start.setHours(0, 0, 0, 0);
      return { startDate: start.toISOString().split('T')[0], endDate };
    }
    
    // If it's a year (4-digit number), return that year's range
    if (/^\d{4}$/.test(range)) {
      const year = parseInt(range);
      return {
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`
      };
    }
    
    // Default: return null for both (all time)
    return { startDate: null, endDate: null };
  };

  const dateRangeParams = getDateRange(timeFrame);
  const { data, loading, error } = useQuery<GetAnalyticsDataResponse>(GET_ANALYTICS_DATA, {
    variables: { startDate: dateRangeParams.startDate, endDate: dateRangeParams.endDate }
  });

  // Determine if we're in local development
  const isLocalDevelopment =
    process.env.NODE_ENV === 'development' ||
    (typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('vercel.app') ||
      window.location.hostname.includes('starfarm.com')
    ));

  const usingMockData = isLocalDevelopment && (!!error || !data?.analyticsData);

  // Process real data from GraphQL
  useEffect(() => {
    if (data?.analyticsData) {
      setAnalyticsData(data.analyticsData);
    }
  }, [data]);

  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} style={{ color: '#1976d2', marginBottom: 16 }} />
          <Typography variant="h6" style={{ color: '#fff' }}>
            Loading analytics data...
          </Typography>
          <Typography variant="body2" style={{ color: '#b0b0b0', marginTop: 8 }}>
            Please wait while we fetch your performance data.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Graceful fallback: if error, show message but continue with mock data
  if (error) {
    console.log('⚠️ Analytics: Error loading data, using mock data', error);
  }

  return (
    <Box className={classes.root}>
      {(usingMockData || error) && (
        <Box mb={2}>
          <SnackbarContent
            message="Using mock analytics data (backend endpoint unavailable). Real data will be displayed when the analytics API is configured."
            style={{ backgroundColor: '#1565c0', color: '#fff' }}
          />
        </Box>
      )}
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.headerTitle}>
          Platform Analytics
        </Typography>
        <Typography variant="body1" className={classes.headerSubtitle}>
          Comprehensive insights into platform performance and user engagement
        </Typography>
      </Box>

      {/* Time Frame Filter */}
      <Box className={classes.filterContainer}>
        <Typography variant="h6" style={{ color: '#fff', fontWeight: 500 }}>
          Analytics Overview
        </Typography>
        <FormControl variant="outlined" className={classes.timeFrameFilter}>
          <InputLabel id="timeframe-select-label">Time Frame</InputLabel>
          <Select
            labelId="timeframe-select-label"
            value={timeFrame}
            onChange={(e) => setTimeFrame(e.target.value as string)}
            label="Time Frame"
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="7d">This Week</MenuItem>
            <MenuItem value="30d">This Month</MenuItem>
            <MenuItem value="90d">Last 90 Days</MenuItem>
            <MenuItem value="1y">This Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} className={classes.statsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#1976d2' }}
              >
                <PlayIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                {analyticsData.totalPlays.toLocaleString()}
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Total Plays
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#4caf50' }}
              >
                <DownloadIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                {analyticsData.totalDownloads.toLocaleString()}
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Total Downloads
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#ff9800' }}
              >
                <MoneyIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                ${analyticsData.totalEarnings.toLocaleString()}
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Total Earnings
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent className={classes.statCardContent}>
              <Avatar 
                className={classes.statIcon} 
                style={{ backgroundColor: '#9c27b0' }}
              >
                <PeopleIcon />
              </Avatar>
              <Typography variant="h3" className={classes.statNumber}>
                {analyticsData.totalArtists.toLocaleString()}
              </Typography>
              <Typography variant="body2" className={classes.statLabel}>
                Total Artists
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Content Grid */}
      <Grid container spacing={3} className={classes.contentGrid}>
        {/* Platform Overview */}
        <Grid item xs={12} lg={6}>
          <Card className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Platform Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h4" style={{ color: '#fff', fontWeight: 600 }}>
                    {analyticsData.totalAlbums}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                    Albums
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h4" style={{ color: '#fff', fontWeight: 600 }}>
                    {analyticsData.totalSongs}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                    Songs
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Daily Performance */}
        <Grid item xs={12} lg={6}>
          <Card className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Daily Performance
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h5" style={{ color: '#1976d2', fontWeight: 600 }}>
                    {Math.round(analyticsData.monthlyPlays / 30).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                    Daily Plays
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h5" style={{ color: '#4caf50', fontWeight: 600 }}>
                    {Math.round(analyticsData.monthlyDownloads / 30).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                    Daily Downloads
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h5" style={{ color: '#ff9800', fontWeight: 600 }}>
                    ${(analyticsData.monthlyEarnings / 30).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                    Daily Earnings
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Monthly Performance */}
        <Grid item xs={12} lg={6}>
          <Card className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Monthly Performance
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h5" style={{ color: '#1976d2', fontWeight: 600 }}>
                    {analyticsData.monthlyPlays.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                    Plays
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h5" style={{ color: '#4caf50', fontWeight: 600 }}>
                    {analyticsData.monthlyDownloads.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                    Downloads
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h5" style={{ color: '#ff9800', fontWeight: 600 }}>
                    ${analyticsData.monthlyEarnings.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                    Earnings
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminAnalytics;
